import { useContext, useMemo } from "react";
import { useNavigate } from "react-router";
import Box from "../../components/Box";
import Button from "../../components/Button";
import Input from "../../components/Input";
import Modal from "../../components/Modal";
import Pagination from "../../components/Pagination";
import useSyncPagination from "../../components/Pagination/hooks/useSyncPagination";
import Table from "../../components/Table";
import { AuthContext } from "../../providers/Auth";
import UserForm from "../forms/User";
import UserDelete from "../forms/User/UserDelete";
import { User } from "../../api/types";
import useGetOrganization from "../../api/useGetOrganization";
import useGetUsers from "../../api/useGetUsers";
import useEntitySelection from "../../hooks/useEntitySelection";
import useFilter from "../../hooks/useFilter";
import collectionCountLabel from "../../utils/collectionCountLabel";
import { checkPermissionAllowed } from "../../utils/verification";
import { usersTable } from "./utils/usersTable";
import style from "./style/Users.module.scss";
import { PiMagnifyingGlass } from "react-icons/pi";

export default function Users() {
  const navigate = useNavigate();
  const sessionContext = useContext(AuthContext);
  const { accessTokenPayload, sessionExists } = sessionContext;
  const { data: organization } = useGetOrganization();
  const organizationId = organization?.id || "";
  const { isLoading, data } = useGetUsers(organizationId);

  // Modals
  const { entityId, action, update, modal } = useEntitySelection();
  const user = data?.find((user) => user.id === entityId);
  const modalContent = useMemo(
    () =>
      !entityId ? (
        <Box className={style.userFormBox}>
          <UserForm action="add" title="Add New User" buttonLabel="Add" onSuccess={modal.handleClose} organizationId={organizationId} user={user} />
        </Box>
      ) : action === "edit" ? (
        <Box className={style.userFormBox}>
          <UserForm action="edit" title="Edit User" buttonLabel="Save" onSuccess={modal.handleClose} organizationId={organizationId} user={user} />
        </Box>
      ) : action === "delete" ? (
        <Box variants={["wide", "space-mid"]}>
          <UserDelete user={user} organizationId={organizationId} onSuccess={modal.handleClose} />
        </Box>
      ) : null,
    // : (
    //   <UserReset user={user} organizationId={organizationId} onSuccess={modal.handleClose} />
    // ),
    [modal.openDelayed]
  );

  // Filter & Pagination
  const { dataFiltered, setFilter } = useFilter<User[]>(["role", "email"], data || []);
  const itemsPerPage = 25;
  const { dataPage, page, paginate, totalItems } = useSyncPagination(dataFiltered as any, itemsPerPage);

  // Parse data to table format
  const tableData = usersTable(dataPage as User[], update);

  if (!checkPermissionAllowed(sessionExists, accessTokenPayload, "users_manage")) {
    navigate("/");
  }

  if (isLoading) {
    return null;
  }

  if (!data) {
    return <>Unable to load users</>;
  }

  return (
    <div className={style.usersPage}>
      <div className="container">
        <div className={style.header}>
          <div className={style.title}>
            <h1>Users</h1>
            <small>
              {collectionCountLabel(
                "No users",
                "user",
                "users",
                data?.length || 0,
                "No",
                `user${dataFiltered.length === 1 ? "" : "s"} found from`,
                dataFiltered.length
              )}
            </small>
          </div>

          <div className={style.actions}>
            <Input
              icon={<PiMagnifyingGlass />}
              type="search"
              autoComplete="off"
              className={style.searchInput}
              placeholder="Search"
              onChange={(e: any) => setFilter(e.target.value)}
            />
            <Button variants={["primary"]} onClick={() => modal.setOpen(true)}>
              Add New User
            </Button>
          </div>
        </div>

        <Table data={tableData} keyAsId="Email" columnWidths={["34%", "30%", "30%", "6%"]} /* sortByColumns={"ALL"} */ />

        {!!(totalItems && totalItems > itemsPerPage) && (
          <Pagination totalItems={totalItems} itemsPerPage={itemsPerPage} onPageChange={paginate} initialPage={page} />
        )}
      </div>

      {modal.openDelayed && (
        <Modal {...modal} useBox={false} useCloseButton>
          {modalContent}
        </Modal>
      )}
    </div>
  );
}
